import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Rocket, 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  HelpCircle,
  Box,
  Cog,
  Zap,
  Cpu,
  Target,
  PartyPopper,
  Wrench
} from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

// Tooltip explanations
const TOOLTIPS = {
  bed_size: "The maximum travel distance of your machine in each direction (X, Y, Z) in millimeters.",
  axis_count: "3-axis machines move X, Y, and Z. 4-axis adds rotation (A-axis) for cylindrical work.",
  motor_class: "NEMA17 are smaller and weaker. NEMA23/34 provide more torque for harder materials.",
  drive_type: "Belt drives are fast but less precise. Ball screws are most accurate. Lead screws are a middle ground.",
  motion_guides: "V-wheels are budget-friendly. Linear rails provide better rigidity and precision.",
  spindle_type: "Trim routers are common on hobby machines. Water-cooled spindles are quieter and more powerful.",
  collet_size: "The maximum tool shank diameter your spindle can hold. 1/4\" (6.35mm) is most common.",
  controller_board: "Your controller firmware affects G-code compatibility and features.",
  rigidity: "Light machines flex under load. Heavy/rigid machines can run faster feeds with better finish.",
};

// Helper component for labels with tooltips
const LabelWithTooltip = ({ children, tooltip }) => (
  <div className="flex items-center gap-1.5">
    <Label className="text-sm text-[#d6d6d6]">{children}</Label>
    {tooltip && (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-4 h-4 text-[#737373] hover:text-[#e67e22] cursor-help transition-colors" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-[#1f1f1f] border-[#3a3a3a] text-[#d6d6d6]">
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
  </div>
);

// Step definitions
const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Rocket, description: 'Let\'s set up your CNC machine' },
  { id: 'basics', title: 'Basic Info', icon: Box, description: 'Name and dimensions' },
  { id: 'motion', title: 'Motion System', icon: Cog, description: 'Axes, drives & rails' },
  { id: 'spindle', title: 'Spindle', icon: Zap, description: 'Router or spindle setup' },
  { id: 'controller', title: 'Controller', icon: Cpu, description: 'Control board & software' },
  { id: 'rigidity', title: 'Rigidity', icon: Target, description: 'Machine strength assessment' },
  { id: 'complete', title: 'Complete', icon: PartyPopper, description: 'Review and finish' },
];

function MachineSetupWizard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [existingMachines, setExistingMachines] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basics
    name: '',
    bed_size_x: '',
    bed_size_y: '',
    bed_size_z: '',
    // Motion
    axis_count: '',
    motor_class: '',
    drive_type_x: '',
    drive_type_y: '',
    drive_type_z: '',
    motion_guides: '',
    // Spindle
    spindle_type_manual: '',
    spindle_power: '',
    max_rpm: '',
    collet_size: '',
    // Controller
    controller_board: '',
    control_software: '',
    // Rigidity
    rigidity: '',
  });

  // Fetch existing machines on mount
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const res = await axios.get(`${API}/machines`);
        setExistingMachines(res.data || []);
      } catch (err) {
        console.error('Failed to load machines', err);
      }
    };
    fetchMachines();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const progress = ((currentStep) / (STEPS.length - 1)) * 100;

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true; // Welcome
      case 1: return formData.name.trim() !== ''; // Basics - name required
      case 2: return true; // Motion - optional
      case 3: return true; // Spindle - optional
      case 4: return true; // Controller - optional
      case 5: return true; // Rigidity - optional
      case 6: return true; // Complete
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Create the machine with all collected data
      const payload = {
        name: formData.name || 'My CNC Machine',
        bed_size_x: parseFloat(formData.bed_size_x) || 0,
        bed_size_y: parseFloat(formData.bed_size_y) || 0,
        bed_size_z: parseFloat(formData.bed_size_z) || 0,
        spindle_power: parseFloat(formData.spindle_power) || 0,
        max_rpm: parseFloat(formData.max_rpm) || 0,
        control_software: formData.control_software || '',
        rigidity: formData.rigidity || 'medium',
      };

      // Create the machine
      const res = await axios.post(`${API}/machines`, payload);
      const machineId = res.data.id;

      // Update with profile fields
      const profilePayload = {};
      const profileFields = [
        'axis_count', 'motor_class', 'drive_type_x', 'drive_type_y', 'drive_type_z',
        'motion_guides', 'spindle_type_manual', 'controller_board', 'rigidity'
      ];
      
      profileFields.forEach(field => {
        if (formData[field] && formData[field] !== 'unknown' && formData[field].trim() !== '') {
          profilePayload[field] = formData[field];
        }
      });

      // Add collet_size as float
      if (formData.collet_size && formData.collet_size !== 'unknown') {
        profilePayload.collet_size = parseFloat(formData.collet_size);
      }

      if (Object.keys(profilePayload).length > 0) {
        await axios.put(`${API}/machines/${machineId}`, profilePayload);
      }

      // Navigate to dashboard or machine setup page
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to save machine', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#a3a3a3]">Loading...</div>
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <div className="text-center space-y-6 py-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#e67e22]/20 flex items-center justify-center">
              <Rocket className="w-10 h-10 text-[#e67e22]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to Machine Setup</h2>
              <p className="text-[#a3a3a3] max-w-md mx-auto">
                We'll guide you through setting up your CNC machine step by step. This helps us give you 
                accurate feeds, speeds, and troubleshooting recommendations.
              </p>
            </div>
            <div className="bg-[#2a2a2a] rounded-lg p-4 max-w-md mx-auto text-left">
              <p className="text-sm text-[#d6d6d6] font-medium mb-2">What we'll cover:</p>
              <ul className="text-sm text-[#a3a3a3] space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Basic machine info</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Motion system (drives & rails)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Spindle or router details</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Controller setup</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Rigidity assessment</li>
              </ul>
            </div>
            {existingMachines.length > 0 && (
              <div className="text-sm text-amber-400">
                You already have {existingMachines.length} machine(s) configured. This wizard will add a new one.
              </div>
            )}
          </div>
        );

      case 1: // Basics
        return (
          <div className="space-y-6 py-4">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Basic Information</h2>
              <p className="text-sm text-[#a3a3a3]">Let's start with the basics about your machine</p>
            </div>
            
            <div className="space-y-4 max-w-md mx-auto">
              <div className="space-y-2">
                <Label className="text-sm text-[#d6d6d6]">Machine Name *</Label>
                <Input
                  data-testid="machine-name-input"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. WorkBee 1010, Shapeoko 4, My CNC"
                  className="bg-black/50 border-[#3a3a3a] text-white"
                />
                <p className="text-xs text-[#737373]">Give your machine a memorable name</p>
              </div>

              <div className="pt-4">
                <LabelWithTooltip tooltip={TOOLTIPS.bed_size}>Work Area (optional)</LabelWithTooltip>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <Label className="text-xs text-[#737373]">X (mm)</Label>
                    <Input
                      data-testid="bed-size-x-input"
                      type="number"
                      value={formData.bed_size_x}
                      onChange={(e) => handleChange('bed_size_x', e.target.value)}
                      placeholder="750"
                      className="bg-black/50 border-[#3a3a3a] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#737373]">Y (mm)</Label>
                    <Input
                      data-testid="bed-size-y-input"
                      type="number"
                      value={formData.bed_size_y}
                      onChange={(e) => handleChange('bed_size_y', e.target.value)}
                      placeholder="750"
                      className="bg-black/50 border-[#3a3a3a] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#737373]">Z (mm)</Label>
                    <Input
                      data-testid="bed-size-z-input"
                      type="number"
                      value={formData.bed_size_z}
                      onChange={(e) => handleChange('bed_size_z', e.target.value)}
                      placeholder="100"
                      className="bg-black/50 border-[#3a3a3a] text-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Motion
        return (
          <div className="space-y-6 py-4">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Motion System</h2>
              <p className="text-sm text-[#a3a3a3]">Tell us about your machine's drive and rail system</p>
            </div>

            <div className="space-y-4 max-w-lg mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <LabelWithTooltip tooltip={TOOLTIPS.axis_count}>Number of Axes</LabelWithTooltip>
                  <Select value={formData.axis_count} onValueChange={(v) => handleChange('axis_count', v)}>
                    <SelectTrigger data-testid="axis-count-select" className="bg-black/50 border-[#3a3a3a] text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                      <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                      <SelectItem value="three_axis" className="text-white">3-axis (X, Y, Z)</SelectItem>
                      <SelectItem value="four_axis" className="text-white">4-axis (+ rotary)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <LabelWithTooltip tooltip={TOOLTIPS.motor_class}>Motor Class</LabelWithTooltip>
                  <Select value={formData.motor_class} onValueChange={(v) => handleChange('motor_class', v)}>
                    <SelectTrigger data-testid="motor-class-select" className="bg-black/50 border-[#3a3a3a] text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                      <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                      <SelectItem value="nema17" className="text-white">NEMA 17 (small)</SelectItem>
                      <SelectItem value="nema23" className="text-white">NEMA 23 (medium)</SelectItem>
                      <SelectItem value="nema34" className="text-white">NEMA 34 (large)</SelectItem>
                      <SelectItem value="small_servos" className="text-white">Small servos</SelectItem>
                      <SelectItem value="industrial_servos" className="text-white">Industrial servos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <LabelWithTooltip tooltip={TOOLTIPS.motion_guides}>Motion Guides / Rails</LabelWithTooltip>
                <Select value={formData.motion_guides} onValueChange={(v) => handleChange('motion_guides', v)}>
                  <SelectTrigger data-testid="motion-guides-select" className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                    <SelectItem value="v_wheels" className="text-white">V-wheels (common on hobby machines)</SelectItem>
                    <SelectItem value="linear_rails" className="text-white">Linear rails (more rigid)</SelectItem>
                    <SelectItem value="round_rails" className="text-white">Round rails</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-2">
                <LabelWithTooltip tooltip={TOOLTIPS.drive_type}>Drive Type per Axis</LabelWithTooltip>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {['x', 'y', 'z'].map(axis => (
                    <div key={axis} className="space-y-1">
                      <Label className="text-xs text-[#737373] uppercase">{axis} Axis</Label>
                      <Select 
                        value={formData[`drive_type_${axis}`]} 
                        onValueChange={(v) => handleChange(`drive_type_${axis}`, v)}
                      >
                        <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                          <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                          <SelectItem value="belt" className="text-white">Belt</SelectItem>
                          <SelectItem value="lead_screw" className="text-white">Lead screw</SelectItem>
                          <SelectItem value="ball_screw" className="text-white">Ball screw</SelectItem>
                          <SelectItem value="rack_and_pinion" className="text-white">Rack & pinion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Spindle
        return (
          <div className="space-y-6 py-4">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Spindle / Router</h2>
              <p className="text-sm text-[#a3a3a3]">What's spinning your bits?</p>
            </div>

            <div className="space-y-4 max-w-md mx-auto">
              <div className="space-y-2">
                <LabelWithTooltip tooltip={TOOLTIPS.spindle_type}>Spindle Type</LabelWithTooltip>
                <Select value={formData.spindle_type_manual} onValueChange={(v) => handleChange('spindle_type_manual', v)}>
                  <SelectTrigger data-testid="spindle-type-select" className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                    <SelectItem value="trim_router" className="text-white">Trim router (Makita/DeWalt)</SelectItem>
                    <SelectItem value="small_water_cooled" className="text-white">Water-cooled (0.8-1.5kW)</SelectItem>
                    <SelectItem value="medium_spindle" className="text-white">Medium spindle (2.2kW)</SelectItem>
                    <SelectItem value="high_power_spindle" className="text-white">High-power (3kW+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-[#d6d6d6]">Power (Watts)</Label>
                  <Input
                    data-testid="spindle-power-input"
                    type="number"
                    value={formData.spindle_power}
                    onChange={(e) => handleChange('spindle_power', e.target.value)}
                    placeholder="e.g. 800"
                    className="bg-black/50 border-[#3a3a3a] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-[#d6d6d6]">Max RPM</Label>
                  <Input
                    data-testid="max-rpm-input"
                    type="number"
                    value={formData.max_rpm}
                    onChange={(e) => handleChange('max_rpm', e.target.value)}
                    placeholder="e.g. 24000"
                    className="bg-black/50 border-[#3a3a3a] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <LabelWithTooltip tooltip={TOOLTIPS.collet_size}>Collet Size (Max Shank)</LabelWithTooltip>
                <Select value={formData.collet_size} onValueChange={(v) => handleChange('collet_size', v)}>
                  <SelectTrigger data-testid="collet-size-select" className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                    <SelectItem value="3.175" className="text-white">1/8" (3.175mm)</SelectItem>
                    <SelectItem value="6.35" className="text-white">1/4" (6.35mm) - Most common</SelectItem>
                    <SelectItem value="8" className="text-white">8mm</SelectItem>
                    <SelectItem value="12.7" className="text-white">1/2" (12.7mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 4: // Controller
        return (
          <div className="space-y-6 py-4">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Controller Setup</h2>
              <p className="text-sm text-[#a3a3a3]">What controls your machine?</p>
            </div>

            <div className="space-y-4 max-w-md mx-auto">
              <div className="space-y-2">
                <LabelWithTooltip tooltip={TOOLTIPS.controller_board}>Controller Board</LabelWithTooltip>
                <Select value={formData.controller_board} onValueChange={(v) => handleChange('controller_board', v)}>
                  <SelectTrigger data-testid="controller-board-select" className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                    <SelectItem value="grbl" className="text-white">GRBL (Arduino/ESP32)</SelectItem>
                    <SelectItem value="grblhal" className="text-white">grblHAL</SelectItem>
                    <SelectItem value="smoothie" className="text-white">Smoothieware</SelectItem>
                    <SelectItem value="mach3" className="text-white">Mach3</SelectItem>
                    <SelectItem value="linuxcnc" className="text-white">LinuxCNC</SelectItem>
                    <SelectItem value="ucnc" className="text-white">UCCNC</SelectItem>
                    <SelectItem value="blackbox" className="text-white">OpenBuilds BlackBox</SelectItem>
                    <SelectItem value="duet" className="text-white">Duet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-[#d6d6d6]">Control Software (optional)</Label>
                <Input
                  data-testid="control-software-input"
                  value={formData.control_software}
                  onChange={(e) => handleChange('control_software', e.target.value)}
                  placeholder="e.g. UGS, CNCjs, OpenBuilds Control"
                  className="bg-black/50 border-[#3a3a3a] text-white"
                />
                <p className="text-xs text-[#737373]">The software you use to send G-code to your machine</p>
              </div>
            </div>
          </div>
        );

      case 5: // Rigidity
        return (
          <div className="space-y-6 py-4">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Machine Rigidity</h2>
              <p className="text-sm text-[#a3a3a3]">How sturdy is your machine?</p>
            </div>

            <div className="space-y-4 max-w-md mx-auto">
              <div className="space-y-2">
                <LabelWithTooltip tooltip={TOOLTIPS.rigidity}>Rigidity Level</LabelWithTooltip>
                <Select value={formData.rigidity} onValueChange={(v) => handleChange('rigidity', v)}>
                  <SelectTrigger data-testid="rigidity-select" className="bg-black/50 border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                    <SelectItem value="unknown" className="text-white">Not sure</SelectItem>
                    <SelectItem value="light" className="text-white">Light (DIY / kit machine)</SelectItem>
                    <SelectItem value="medium" className="text-white">Medium (hobbyist machine)</SelectItem>
                    <SelectItem value="heavy" className="text-white">Heavy (rigid / semi-pro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-[#2a2a2a] rounded-lg p-4 mt-4">
                <p className="text-sm text-[#d6d6d6] font-medium mb-2">Why does rigidity matter?</p>
                <ul className="text-xs text-[#a3a3a3] space-y-1">
                  <li>• <span className="text-amber-400">Light machines</span> need slower feeds to avoid chatter</li>
                  <li>• <span className="text-blue-400">Medium machines</span> handle most hobby work well</li>
                  <li>• <span className="text-emerald-400">Heavy machines</span> can push harder and faster</li>
                </ul>
                <p className="text-xs text-[#737373] mt-2">
                  We'll use this to give you conservative, safe starting points.
                </p>
              </div>
            </div>
          </div>
        );

      case 6: // Complete
        return (
          <div className="text-center space-y-6 py-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
              <PartyPopper className="w-10 h-10 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">All Done!</h2>
              <p className="text-[#a3a3a3] max-w-md mx-auto">
                Your machine "{formData.name || 'CNC Machine'}" is ready to be saved. You can always 
                update these settings later from the Equipment page.
              </p>
            </div>

            <div className="bg-[#2a2a2a] rounded-lg p-4 max-w-md mx-auto text-left">
              <p className="text-sm text-[#d6d6d6] font-medium mb-3">Quick Summary:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-[#737373]">Name:</div>
                <div className="text-white">{formData.name || 'Not set'}</div>
                
                <div className="text-[#737373]">Work Area:</div>
                <div className="text-white">
                  {formData.bed_size_x && formData.bed_size_y 
                    ? `${formData.bed_size_x} x ${formData.bed_size_y} mm`
                    : 'Not set'}
                </div>
                
                <div className="text-[#737373]">Spindle:</div>
                <div className="text-white capitalize">
                  {formData.spindle_type_manual?.replace(/_/g, ' ') || 'Not set'}
                </div>
                
                <div className="text-[#737373]">Rigidity:</div>
                <div className="text-white capitalize">{formData.rigidity || 'Not set'}</div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
    >
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#e67e22] mb-2">
          <Wrench className="w-5 h-5" />
          <span className="text-xs font-bold uppercase tracking-wider">Setup Wizard</span>
        </div>
        <h1 
          className="text-2xl md:text-3xl font-bold text-[#e67e22] tracking-tight uppercase"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Machine Setup Wizard
        </h1>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#737373]">Step {currentStep + 1} of {STEPS.length}</span>
            <span className="text-xs text-[#e67e22]">{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-[#2a2a2a]" />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-6 overflow-x-auto pb-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex flex-col items-center min-w-[60px] ${
                  isActive ? 'opacity-100' : isCompleted ? 'opacity-80' : 'opacity-40'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  isActive 
                    ? 'bg-[#e67e22] text-white' 
                    : isCompleted 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-[#2a2a2a] text-[#737373]'
                }`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-[10px] text-center ${isActive ? 'text-white' : 'text-[#737373]'}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Main Card */}
        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardContent className="p-6">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <div>
            {currentStep > 0 && (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="border-[#3a3a3a] text-[#a3a3a3] hover:text-white"
                data-testid="wizard-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="text-[#737373] hover:text-white"
              data-testid="wizard-skip-btn"
            >
              Skip for now
            </Button>

            {currentStep < STEPS.length - 1 ? (
              <Button 
                onClick={handleNext}
                disabled={!canProceed()}
                className="bg-[#e67e22] hover:bg-[#d35400] text-white"
                data-testid="wizard-next-btn"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-save-btn"
              >
                {saving ? 'Saving...' : 'Save & Finish'}
                <Check className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MachineSetupWizard;
